/**
 * usePracticeBroadcast — CORE-736. Publishes the library practice the master
 * plays during a live into the RTC channel, so the cloud recording contains it.
 *
 * Why this exists at all: the practice is NOT in the channel. Every client — the
 * master and each viewer — plays its own `<video>` from the same URL, kept in
 * step over SignalR (`useLivePracticePlayback`). That is the right design for
 * watching: full quality, no extra load on the master's uplink. But the cloud
 * recorder records the CHANNEL, so the archive showed the master talking over
 * something no one watching the recording could see.
 *
 * The fix mirrors the screen share (`useScreenShare`): a SECOND Agora client
 * with its own UID (`cameraUid + PracticeUidOffset`) and its own token, joining
 * the same channel. The tracks come from `video.captureStream()` on the element
 * the master is already watching, so no second decode and no second download.
 * Viewers are told this UID in their token response and skip it — they keep
 * their local copy rather than a re-encoded one, and never hear the audio twice.
 *
 * Lifecycle is tied to what the recording should show: publish while the clip is
 * PLAYING, unpublish when it pauses, stops, or the broadcast ends. The backend
 * flag flips with it and re-renders the recording layout (practice big, speakers
 * beside it — the same shape a screen share produces).
 *
 * CORE-737 — on a LiveKit live there is no second client: the same captured
 * tracks are published on the master's existing room connection under a marked
 * publication name, and room-composite egress records the room. Viewers match
 * that name and skip it, exactly as they skip the Agora practice uid.
 *
 * CORE-1361 — the same hook now serves practice ROOMS. The defect was identical
 * there (room 3931 on dev recorded 650 s of digital silence with no video track
 * at all), but the room branch had none of this: `LiveRoomScreen` plays the clip
 * in its own element and publishes nothing. Only three things differ between the
 * two surfaces — where the second-client token comes from, whether there is a
 * backend flag to flip, and the telemetry namespace — so they are parameters
 * rather than a second copy of this lifecycle.
 *
 * Web only. `captureStream()` is a browser API, and the master's practice
 * controls are HTML5-`<video>` specific anyway.
 */
import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { safeImport } from '../../utils/staleBundle';
import { trackRtc, trackRtcError, RtcContext } from '../../services/liveTelemetry';
import { applyRtcProxyIfActive } from '../../services/rtc/rtcProxy';

/** CORE-1361 — the publisher token, in the shape both surfaces reduce to. */
export interface PracticeBroadcastToken {
    appId: string;
    channelName: string;
    token: string;
    uid: number;
}

/**
 * CORE-1361 — telemetry namespace per surface. Spelled out rather than
 * assembled from a prefix so both event names stay greppable in source.
 */
const PRACTICE_EVENTS = {
    live: {
        published: 'live_rtc_master.practice_published',
        unpublished: 'live_rtc_master.practice_unpublished',
        unavailable: 'live_rtc_master.practice_capture_unavailable',
        publishFailed: 'live_rtc_master.practice_publish_failed',
        stateFailed: 'live_rtc_master.practice_state_failed',
    },
    room: {
        published: 'room_rtc.practice_published',
        unpublished: 'room_rtc.practice_unpublished',
        unavailable: 'room_rtc.practice_capture_unavailable',
        publishFailed: 'room_rtc.practice_publish_failed',
        stateFailed: 'room_rtc.practice_state_failed',
    },
} as const;

interface UsePracticeBroadcastParams {
    /** CORE-1361 — which surface this is, for the telemetry namespace only. */
    surface?: 'live' | 'room';
    /**
     * CORE-1361 — mint the publisher token for the practice uid. Lives use the
     * master endpoint, rooms the room one; both refuse before a recording is
     * running, because without a recorder there is nothing to publish FOR.
     */
    fetchToken: () => Promise<PracticeBroadcastToken>;
    /**
     * CORE-1361 — lives flip `Live.IsPracticeStreaming` so Agora cloud recording
     * re-renders its layout with the clip in the big tile. Rooms have no such
     * flag: they are recorded by the audio bridge or by room-composite egress,
     * and neither takes a layout instruction. Omitted → nothing to flip.
     */
    setStreamState?: (active: boolean) => Promise<void>;
    /** Telemetry context of the owning publisher. */
    rtcCtx: RtcContext;
    /** True once the master is actually broadcasting (publisher state 'live'). */
    broadcasting: boolean;
    /** True while the clip is playing — i.e. while viewers are watching it. */
    practicePlaying: boolean;
    /** The element the master is watching. Null until the overlay mounts. */
    videoEl: HTMLVideoElement | null;
    /**
     * Identity of the clip currently loaded. A swap mid-session re-captures —
     * the old stream ends. Lives pass the URL; rooms pass the media identity,
     * which is what changes there when the host swaps the practice.
     */
    practiceKey: string | null;
    /** CORE-393 — this live runs on LiveKit, so publishing goes through `livekit`. */
    isLiveKit?: boolean;
    /**
     * CORE-737 — LiveKit publishes on the SAME room connection (no second client,
     * no separate token), so the engine has to hand us its publish hooks. Absent
     * or null on an Agora live.
     */
    livekit?: {
        publish: (video: MediaStreamTrack | null, audio: MediaStreamTrack | null) => Promise<void>;
        unpublish: () => Promise<void>;
    } | null;
}

/**
 * Grab a MediaStream off a media element. Chrome/Edge expose `captureStream`,
 * Firefox `mozCaptureStream`; Safari exposes neither, so masters there keep the
 * live behaviour they have today and only the recording misses the clip.
 */
function captureElement(el: HTMLVideoElement): MediaStream | null {
    const anyEl = el as any;
    const fn = anyEl.captureStream ?? anyEl.mozCaptureStream;
    if (typeof fn !== 'function') return null;
    try {
        return fn.call(el) as MediaStream;
    } catch {
        // Thrown when the element is CORS-tainted — the resource loaded, but
        // not cleanly enough to read pixels out of.
        return null;
    }
}

/**
 * Resolve once the element has actually decoded a frame we can capture, or
 * false if it never does within the window.
 *
 * Why this exists: the publish effect fires the moment the SignalR-synced
 * playback state flips to "playing". When the overlay mounts already-playing
 * (state restored from the server), that is BEFORE the freshly mounted
 * `<video>` — reloaded once for `crossOrigin` — has its first frame. captured
 * then, the stream has zero video tracks. This waits for the element to become
 * decodable so the re-capture actually carries video.
 */
function waitForVideoFrame(el: HTMLVideoElement, timeoutMs = 8000): Promise<boolean> {
    // HAVE_CURRENT_DATA and a real frame size means a frame is already there.
    if (el.readyState >= 2 && el.videoWidth > 0) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
        let done = false;
        const onReady = () => { if (!done && el.videoWidth > 0) finish(true); };
        const finish = (ok: boolean) => {
            if (done) return;
            done = true;
            el.removeEventListener('loadeddata', onReady);
            el.removeEventListener('canplay', onReady);
            el.removeEventListener('playing', onReady);
            el.removeEventListener('timeupdate', onReady);
            clearTimeout(timer);
            resolve(ok);
        };
        el.addEventListener('loadeddata', onReady);
        el.addEventListener('canplay', onReady);
        el.addEventListener('playing', onReady);
        el.addEventListener('timeupdate', onReady);
        const timer = setTimeout(() => finish(false), timeoutMs);
    });
}

/**
 * Capture a stream that actually has a video track. First attempt is immediate;
 * if the element hasn't produced a frame yet, wait for one and re-capture. A
 * null return means the browser cannot capture at all (Safari, or a CORS-tainted
 * element) — `captureElement` returned null both times.
 */
async function captureReadyStream(el: HTMLVideoElement): Promise<MediaStream | null> {
    const first = captureElement(el);
    if (!first) return null; // capture unsupported / tainted — nothing to wait for
    if (first.getVideoTracks().length > 0) return first;
    // Stream but no video track: the element hasn't decoded a frame yet. Drop
    // this empty capture, wait for a frame, then re-capture.
    try { first.getTracks().forEach((t) => t.stop()); } catch { /* nothing to stop */ }
    const ready = await waitForVideoFrame(el);
    if (!ready) return null;
    const second = captureElement(el);
    if (!second || second.getVideoTracks().length === 0) return null;
    return second;
}

export function usePracticeBroadcast({
    surface = 'live',
    fetchToken,
    setStreamState,
    rtcCtx,
    broadcasting,
    practicePlaying,
    videoEl,
    practiceKey,
    isLiveKit,
    livekit,
}: UsePracticeBroadcastParams): void {
    const events = PRACTICE_EVENTS[surface];
    const clientRef = useRef<any>(null);
    const videoTrackRef = useRef<any>(null);
    const audioTrackRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const activeRef = useRef(false);
    // Start and stop must never interleave: a quick pause→play would otherwise
    // publish onto a client the stop path is still tearing down. Every
    // transition is queued onto one chain instead of guarded by a boolean.
    const chainRef = useRef<Promise<void>>(Promise.resolve());
    // CORE-1361 — the two injected callbacks live in refs, NOT in the dependency
    // lists below. A caller that forgets to memoise one would otherwise rebuild
    // `start`/`stop` on every render and re-run the publish effect with them,
    // which is exactly the class of breakage that a dependency list quietly
    // introduces into a lifecycle that must survive re-renders untouched.
    const fetchTokenRef = useRef(fetchToken);
    fetchTokenRef.current = fetchToken;
    const setStreamStateRef = useRef(setStreamState);
    setStreamStateRef.current = setStreamState;

    const start = useCallback(async (el: HTMLVideoElement) => {
        if (activeRef.current) return;

        const stream = await captureReadyStream(el);
        const videoTrack = stream?.getVideoTracks()[0] ?? null;
        if (!stream || !videoTrack) {
            // Not fatal to the live in any way — the master and the viewers see
            // the practice exactly as before. Only the recording loses it, and
            // this event is the one thing that says so. Re-probe to tell apart
            // "browser can't capture" (Safari/tainted) from "element still had no
            // frame after the wait" — different fixes, so worth logging apart.
            trackRtc(events.unavailable, rtcCtx, {
                reason: captureElement(el) ? 'no_video_track' : 'capture_unsupported',
            });
            return;
        }
        const audioTrack = stream.getAudioTracks()[0] ?? null;

        // CORE-737 — on LiveKit the clip rides the master's existing room
        // connection with a marked publication name; room-composite egress
        // records the room, so that is all the recorder needs. No second client,
        // no token, and no backend flag: egress lays the room out itself.
        if (isLiveKit) {
            if (!livekit) {
                trackRtc(events.unavailable, rtcCtx, { reason: 'livekit_engine_missing' });
                return;
            }
            await livekit.publish(videoTrack, audioTrack);
            streamRef.current = stream;
            activeRef.current = true;
            trackRtc(events.published, rtcCtx, { provider: 'livekit', withAudio: !!audioTrack });
            return;
        }

        const tokenRes = await fetchTokenRef.current();
        const AgoraRTC = (await safeImport(() => import('agora-rtc-sdk-ng'))).default;

        // Custom tracks: the media already exists, Agora only wraps it. The
        // bitrate cap keeps a 1080p clip from crowding out the master's camera
        // on the same uplink — the recording is 720p anyway.
        const customVideo = AgoraRTC.createCustomVideoTrack({
            mediaStreamTrack: videoTrack,
            bitrateMax: 1500,
        });
        const customAudio = audioTrack
            ? AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack })
            : null;

        const client = AgoraRTC.createClient({ mode: 'live', codec: 'vp8', role: 'host' });
        try {
            // CORE-1420 — same path as the master's camera client.
            await applyRtcProxyIfActive(client);
            await client.join(tokenRes.appId, tokenRes.channelName, tokenRes.token, tokenRes.uid);
            await client.publish(customVideo);
            if (customAudio) await client.publish(customAudio);
        } catch (e) {
            try { await client.leave(); } catch { /* never joined */ }
            try { customVideo.close(); } catch { /* already closed */ }
            try { customAudio?.close(); } catch { /* already closed */ }
            throw e;
        }

        clientRef.current = client;
        videoTrackRef.current = customVideo;
        audioTrackRef.current = customAudio;
        streamRef.current = stream;
        activeRef.current = true;

        // Only now tell the backend: the flag drives the recording layout, and
        // claiming the tile before the track is up would record a black frame.
        try { await setStreamStateRef.current?.(true); }
        catch (e) { trackRtcError(events.stateFailed, rtcCtx, e, { active: true }); }

        trackRtc(events.published, rtcCtx, {
            practiceUid: tokenRes.uid,
            withAudio: !!customAudio,
        });
    }, [events, rtcCtx, isLiveKit, livekit]);

    const stop = useCallback(async () => {
        if (!activeRef.current) return;
        activeRef.current = false;

        if (isLiveKit) {
            try { await livekit?.unpublish(); } catch { /* room already gone */ }
            try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
            streamRef.current = null;
            trackRtc(events.unpublished, rtcCtx, { provider: 'livekit' });
            return;
        }

        const client = clientRef.current;
        const video = videoTrackRef.current;
        const audio = audioTrackRef.current;
        clientRef.current = null;
        videoTrackRef.current = null;
        audioTrackRef.current = null;

        if (client) {
            try {
                if (video) await client.unpublish(video);
                if (audio) await client.unpublish(audio);
            } catch { /* leaving below drops them anyway */ }
            try { await client.leave(); } catch { /* already gone */ }
        }
        try { video?.close(); } catch { /* already closed */ }
        try { audio?.close(); } catch { /* already closed */ }
        // Release the capture itself — otherwise Chrome keeps encoding frames
        // off an element nobody is publishing.
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
        streamRef.current = null;

        try { await setStreamStateRef.current?.(false); }
        catch (e) { trackRtcError(events.stateFailed, rtcCtx, e, { active: false }); }

        trackRtc(events.unpublished, rtcCtx);
    }, [events, rtcCtx, isLiveKit, livekit]);

    useEffect(() => {
        if (Platform.OS !== 'web') return;

        const enqueue = (fn: () => Promise<void>) => {
            chainRef.current = chainRef.current
                .then(fn)
                .catch((e) => { trackRtcError(events.publishFailed, rtcCtx, e); });
        };

        const shouldPublish = broadcasting && practicePlaying && !!videoEl && !!practiceKey;
        if (shouldPublish) enqueue(() => start(videoEl!));
        else enqueue(stop);
        // `practiceKey` is a dependency on purpose: swapping the clip mid-session
        // ends the captured tracks, so the publish has to be rebuilt around the
        // new ones rather than left pointing at a dead stream.
    }, [isLiveKit, broadcasting, practicePlaying, videoEl, practiceKey, start, stop, rtcCtx, events]);

    // Tab closed / navigated away mid-practice — drop the second client so it
    // doesn't linger in the channel as a frozen tile in the recording.
    useEffect(() => {
        return () => {
            if (!activeRef.current) return;
            chainRef.current = chainRef.current.then(stop).catch(() => { /* unmounting */ });
        };
    }, [stop]);
}
